import { PERCUSSION, TUNABLE } from '@/lib/pitch';
import { TIMBRES } from '@/lib/droneTimbres';
import { encodeSteps, parseSteps, SONG_PARAM } from '@/lib/droneSongs';

const PARAM = 'tuning';
const KIT_PARAM = 'kit';
const EXERCISE_PARAM = 'exercise';

export const toB64 = (s) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
export const fromB64 = (s) => {
  const std = s.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(std + '='.repeat((4 - (std.length % 4)) % 4))));
};

export function buildTuningShareUrl(instrumentId, { name, notes }) {
  const url = new URL('/tuner', window.location.origin);
  url.searchParams.set(PARAM, toB64(JSON.stringify({ i: instrumentId, n: name, s: notes })));
  return url.toString();
}

// Returns { instrumentId, name, notes } or null if the URL has no valid shared tuning.
export function readSharedTuning(search) {
  const raw = new URLSearchParams(search).get(PARAM);
  if (!raw) return null;
  try {
    const { i, n, s } = JSON.parse(fromB64(raw));
    const spec = TUNABLE[i];
    const validNotes = Array.isArray(s) && s.every((m) => Number.isInteger(m) && m >= 12 && m <= 96);
    if (!spec || !validNotes || !spec.counts.includes(s.length)) return null;
    return { instrumentId: i, name: String(n || 'Shared').slice(0, 40), notes: s };
  } catch {
    return null;
  }
}

export function buildKitShareUrl(instrumentId, notes) {
  const url = new URL('/tuner', window.location.origin);
  url.searchParams.set(KIT_PARAM, toB64(JSON.stringify({ i: instrumentId, s: notes })));
  return url.toString();
}

// Returns { kind: 'kit', instrumentId, notes } or null; notes must fit each drum's range.
export function readSharedKit(search) {
  const raw = new URLSearchParams(search).get(KIT_PARAM);
  if (!raw) return null;
  try {
    const { i, s } = JSON.parse(fromB64(raw));
    const drums = PERCUSSION[i];
    if (!drums || !Array.isArray(s) || s.length !== drums.length) return null;
    if (!s.every((m, k) => Number.isInteger(m) && m >= drums[k].min && m <= drums[k].max)) return null;
    return { kind: 'kit', instrumentId: i, notes: s };
  } catch {
    return null;
  }
}

export const CLICK_SIGS = ['2/4', '3/4', '4/4', '5/4', '6/8', '7/8'];
const readLS = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k) || 'null'); return v ?? d; } catch { return d; } };

// Current interval-trainer and click settings, as stored by their components.
const currentTrainer = () => ({
  i: readLS('tuner.intervals', [3, 4, 5, 7, 12]),
  d: readLS('tuner.intervalDir', 'up'),
  t: !!readLS('tuner.intervalTimed', false),
  b: readLS('tuner.intervalBars', 1),
  sc: !!readLS('tuner.intervalInScale', false),
});
const currentClick = () => { const c = readLS('tuner.click', {}); return { bpm: c.bpm || 80, sig: c.sig || '4/4' }; };

export function buildExerciseShareUrl(cfg, scale) {
  const url = new URL('/bordone', window.location.origin);
  const payload = {
    p: cfg.pc, o: cfg.octave, f: !!cfg.fifth, t: cfg.temperament === 'just' ? 'just' : 'equal', k: cfg.transpose || 'C',
    ...(scale ? { n: scale.name, s: scale.steps } : {}),
    ...(cfg.prog?.enabled ? { g: { s: encodeSteps(cfg.prog.steps), tm: cfg.timbre || 'organ', v: cfg.voicing || (cfg.fifth ? 'fifth' : 'root') } } : {}),
    x: currentTrainer(),
    c: currentClick(),
  };
  url.searchParams.set(EXERCISE_PARAM, toB64(JSON.stringify(payload)));
  return url.toString();
}

const parseTrainer = (x) => {
  if (!x) return null;
  const okI = Array.isArray(x.i) && x.i.length > 0 && x.i.every((s) => Number.isInteger(s) && s >= 1 && s <= 12);
  if (!okI || !['up', 'down', 'mixed'].includes(x.d) || ![1, 2, 4].includes(x.b)) return null;
  return { intervals: [...new Set(x.i)].sort((a, b) => a - b), direction: x.d, timed: !!x.t, barsPer: x.b, inScale: !!x.sc };
};
const parseClick = (c) => {
  if (!c || !Number.isInteger(c.bpm) || c.bpm < 30 || c.bpm > 300 || !CLICK_SIGS.includes(c.sig)) return null;
  return { bpm: c.bpm, sig: c.sig };
};
// Chord sequence carried by an exercise (timbre + voicing + steps).
const parseExerciseSong = (g) => {
  const steps = g && parseSteps(g.s);
  if (!steps) return null;
  return { steps, timbre: TIMBRES.some((x) => x.id === g.tm) ? g.tm : 'organ', voicing: ['root', 'fifth', 'chord'].includes(g.v) ? g.v : 'chord' };
};

// Returns { kind: 'exercise', drone, scale|null, trainer|null, click|null } or null.
export function readSharedExercise(search) {
  const raw = new URLSearchParams(search).get(EXERCISE_PARAM);
  if (!raw) return null;
  try {
    const { p, o, f, t, n, s, x, c, k, g } = JSON.parse(fromB64(raw));
    const hasScale = s !== undefined;
    const okSteps = Array.isArray(s) && s.length >= 2 && s[0] === 0
      && s.every((v, i) => Number.isInteger(v) && v >= 0 && v <= 11 && (i === 0 || v > s[i - 1]));
    if (!Number.isInteger(p) || p < 0 || p > 11 || !Number.isInteger(o) || o < 1 || o > 5 || (hasScale && !okSteps)) return null;
    const trainer = parseTrainer(x);
    if (!hasScale && !trainer) return null;
    return {
      kind: 'exercise',
      drone: { pc: p, octave: o, fifth: !!f, temperament: t === 'just' ? 'just' : 'equal', ...(['C', 'Bb', 'Eb', 'F'].includes(k) ? { transpose: k } : {}) },
      scale: hasScale ? { name: String(n || 'Shared scale').slice(0, 40), steps: s } : null,
      trainer,
      click: parseClick(c),
      song: parseExerciseSong(g),
    };
  } catch {
    return null;
  }
}

// Applies shared trainer/click settings to storage (components remount to read them).
export function applySharedSettings({ trainer, click }) {
  if (trainer) {
    localStorage.setItem('tuner.intervals', JSON.stringify(trainer.intervals));
    localStorage.setItem('tuner.intervalDir', JSON.stringify(trainer.direction));
    localStorage.setItem('tuner.intervalTimed', JSON.stringify(trainer.timed));
    localStorage.setItem('tuner.intervalBars', JSON.stringify(trainer.barsPer));
    localStorage.setItem('tuner.intervalInScale', JSON.stringify(trainer.inScale));
  }
  if (click) localStorage.setItem('tuner.click', JSON.stringify({ ...readLS('tuner.click', {}), ...click }));
}

export function clearSharedTuningParam() {
  const url = new URL(window.location.href);
  url.searchParams.delete(PARAM);
  url.searchParams.delete(KIT_PARAM);
  url.searchParams.delete(EXERCISE_PARAM);
  url.searchParams.delete(SONG_PARAM);
  window.history.replaceState(null, '', url.pathname + url.search);
}
